// API Service to connect frontend to backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3007/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method for API calls
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Health check
  async checkHealth() {
    return this.request('/health');
  }

  // Assistant folder monitoring
  async startFolderMonitoring(assistantId, folderPath) {
    return this.request(`/assistants/${assistantId}/monitor`, {
      method: 'POST',
      body: JSON.stringify({ folderPath }),
    });
  }

  async stopFolderMonitoring(assistantId) {
    return this.request(`/assistants/${assistantId}/monitor`, {
      method: 'DELETE',
    });
  }

  // Get documents for an assistant
  async getDocuments(assistantId) {
    return this.request(`/assistants/${assistantId}/documents`);
  }

  // Get stats for an assistant
  async getStats(assistantId) {
    return this.request(`/assistants/${assistantId}/stats`);
  }

  // Chat with documents
  async chatWithDocuments(assistantId, message, conversationHistory = []) {
    return this.request(`/assistants/${assistantId}/chat`, {
      method: 'POST',
      body: JSON.stringify({
        message,
        conversationHistory: conversationHistory.map(msg => ({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date().toISOString()
        }))
      }),
    });
  }

  // Upload files (alternative to folder monitoring)
  async uploadFiles(assistantId, files) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    return this.request(`/assistants/${assistantId}/upload`, {
      method: 'POST',
      headers: {}, // Let browser set content-type for FormData
      body: formData,
    });
  }

  // Search documents
  async searchDocuments(assistantId, query) {
    return this.request(`/assistants/${assistantId}/search?q=${encodeURIComponent(query)}`);
  }

  // Get conversation history
  async getConversationHistory(assistantId) {
    return this.request(`/assistants/${assistantId}/history`);
  }

  // Clear conversation history
  async clearConversationHistory(assistantId) {
    return this.request(`/assistants/${assistantId}/history`, {
      method: 'DELETE',
    });
  }

  // Get all monitored assistants
  async getMonitoredAssistants() {
    return this.request('/assistants');
  }
}

const apiService = new ApiService();
export default apiService;